<script setup lang="ts">
// Full-page help overlay for anyone standing at the kiosk (DESIGN.md section 9.1, "Help").
//
// 2026-08-31 round-3 feedback item 7 — reworked from the original small-card/tap-anywhere-to-
// dismiss design:
//  (a) the WHOLE PAGE scrolls now (no small inner scrollable rules box) — the overlay can hold
//      more content than 960×540 without clipping or nesting a second scroll region.
//  (b) structure: "How to play" (concise, THIS kiosk's actual flow — derived from DESIGN.md
//      sections 9.1/9.4/6.1, not invented) comes first, the official ITTF rules (rules.ts)
//      follow below — both always visible via scroll, no more show/hide toggle.
//  (c) "tap anywhere to close" is gone — a page that scrolls can't ALSO dismiss on every tap
//      (you'd never be able to scroll it), so there's now an explicit Back button instead, sticky
//      at the top so it stays reachable while scrolled down.
//  (d) i18n: es-ES is the default (src/i18n.ts), with an EN toggle next to Back — a small local
//      strings map, not a framework; scoped to this component's own content.
import { mdiArrowLeft, mdiTranslate } from '@mdi/js'
import { t, toggleLocale, useLocale } from '../i18n'
import { TABLE_TENNIS_RULES } from '../rules'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'dismiss'): void }>()

const locale = useLocale()
const howToPlaySteps = [
  'howToPlayStep1',
  'howToPlayStep2',
  'howToPlayStep3',
  'howToPlayStep4',
  'howToPlayStep5',
  'howToPlayStep6',
  'howToPlayStep7',
] as const
</script>

<template>
  <div v-if="open" class="fixed inset-0 z-[80] bg-neutral-950 flex flex-col overflow-y-auto">
    <div class="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-neutral-950/95 border-b border-neutral-800 backdrop-blur">
      <button
        type="button"
        class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold flex items-center gap-2"
        @click="emit('dismiss')"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
        {{ t('back') }}
      </button>
      <div class="flex-1"></div>
      <button
        type="button"
        class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold flex items-center gap-2 uppercase text-sm"
        :aria-label="locale.locale === 'es' ? 'Switch to English' : 'Cambiar a español'"
        @click="toggleLocale"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor"><path :d="mdiTranslate" /></svg>
        {{ locale.locale === 'es' ? 'EN' : 'ES' }}
      </button>
    </div>

    <div class="flex-1 max-w-[640px] w-full mx-auto p-5 flex flex-col gap-6">
      <section class="flex flex-col gap-3">
        <h1 class="text-2xl font-black text-emerald-400">{{ t('howToPlayTitle') }}</h1>
        <ol class="flex flex-col gap-3">
          <li v-for="(key, i) in howToPlaySteps" :key="key" class="flex gap-3 text-base leading-snug text-neutral-200">
            <span class="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-neutral-950 font-bold text-sm flex items-center justify-center">{{ i + 1 }}</span>
            <span class="pt-0.5">{{ t(key) }}</span>
          </li>
        </ol>
      </section>

      <section class="flex flex-col gap-3 pb-6">
        <h2 class="text-lg font-bold text-neutral-100">{{ t('rulesTitle') }}</h2>
        <div class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4">
          <p class="text-[11px] font-mono text-neutral-600 uppercase tracking-wide mb-2">{{ t('rulesSubtitle') }}</p>
          <ul class="flex flex-col gap-2">
            <li v-for="rule in TABLE_TENNIS_RULES[locale.locale]" :key="rule" class="flex gap-2 text-sm leading-snug text-neutral-300">
              <span class="text-emerald-400 shrink-0">•</span>
              <span>{{ rule }}</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>
